'use client';

import { getAuthorDetail } from '@/api/blog';
import {
  IBlogArticleListItem,
  IBlogAuthorInfo,
  IBlogAuthorResponse,
  IBlogConfig,
} from '@/api/types/blog';
import Breadcrumbs from '@/components/Breadcrumbs';
import Image from '@/components/Image';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useStore } from '@/providers/app-store-provider';
import { colors } from '@/theme/zeelool.theme';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useParams } from 'next/navigation';
import { useCallback, useContext, useState } from 'react';
import AuthorArticleList from '../../components/AuthorArticleList';
import { useBlogAuthorDetailTrack } from '../../hooks/blogAuthorDetailTrack';

interface InitialData {
  articles: IBlogArticleListItem[];
  total: number;
  authorInfo: IBlogAuthorInfo;
  navList: IBlogConfig[];
}

export default function AuthorDetailClient({ initialData }: { initialData: InitialData }) {
  const { name } = useParams();
  const { authorInfo, navList } = initialData;
  const { deviceType } = useContext(ResponsiveContext);
  const isWeb = deviceType === EPlatform.WEB;
  const waiMode = useStore((state) => state.waiMode);
  const { sdBlogAuthorDetailsPageArticleExp, sdBlogAuthorDetailsPageArticleClick } =
    useBlogAuthorDetailTrack();

  // 页面状态管理
  const [articles, setArticles] = useState<IBlogArticleListItem[]>(initialData.articles || []);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(initialData.total || 0);
  const [loading, setLoading] = useState(false);

  // 获取作者文章列表
  const fetchArticles = useCallback(
    async (pageNum: number, shouldAppend = false) => {
      if (!name) return null;

      try {
        setLoading(true);
        const response = (await getAuthorDetail({
          author_path: name as string,
          page: pageNum,
          limit: 18,
        })) as IBlogAuthorResponse;

        const {
          code,
          data: {
            data: { data: newArticles = [], total: newTotal = 0 },
          },
        } = response;

        if (code === 200) {
          if (shouldAppend) {
            // 追加数据（移动端无限滚动）
            setArticles((prevArticles) => [...prevArticles, ...newArticles]);
          } else {
            // 替换数据（PC端分页）
            setArticles(newArticles);
          }
          setTotal(newTotal);
        }
        return response;
      } catch (error) {
        logger.error('获取作者文章列表失败:', error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [name],
  );

  // 处理页面变化
  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      fetchArticles(newPage, false);
    },
    [fetchArticles],
  );

  // 处理加载更多
  const handleLoadMore = useCallback(() => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchArticles(nextPage, true);
  }, [page, fetchArticles]);

  return (
    <div className={`min-h-screen bg-[#f4f4f4] ${isWeb ? 'pt-12 px-48' : ''}`}>
      {/* 面包屑导航 */}
      <Breadcrumbs
        items={[
          { label: 'Home', href: '/' },
          { label: 'ZEELOOL Blog', href: '/blog' },
          { label: 'Authors', href: '/blog/authors' },
          { label: authorInfo.name || 'Author', href: `/blog/authors/${name}` },
        ]}
        className={`${isWeb ? 'pt-12' : 'pl-14 pt-10'}`}
        sx={{
          '& .MuiBreadcrumbs-li p, & .MuiBreadcrumbs-li a span, & .MuiBreadcrumbs-separator': {
            fontSize: '12px',
            color: colors.primaryBlack,
          },
          '& .MuiBreadcrumbs-li a': {
            display: 'block',
            fontSize: '12px',
            lineHeight: '1.5',
          },
        }}
      />

      <div
        className={`${
          isWeb
            ? 'max-w-[1496px] mx-auto pt-40 [@media(max-width:1586px)]:w-full'
            : 'px-14 m-auto py-20'
        }`}
      >
        {authorInfo.id && (
          <div
            className={`${
              isWeb
                ? 'bg-[#e2f8b5] flex items-center px-40 py-[45px] pr-80'
                : 'bg-[#e3ffab] px-12 py-20'
            }`}
          >
            <div
              className={`flex items-center ${isWeb ? 'w-[calc(100%-560px-120px-40px)] [@media(max-width:1279px)]:w-[calc(100%-40%-120px-40px)]' : 'gap-8'}`}
            >
              <div
                className={`rounded-full overflow-hidden flex-shrink-0 ${
                  isWeb ? 'w-[180px] h-[180px]' : 'w-40 h-40'
                }`}
              >
                <Image
                  src={authorInfo.head_portrait_url}
                  alt={authorInfo.name}
                  width={isWeb ? 180 : 40}
                  height={isWeb ? 180 : 40}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className={`${isWeb ? 'ml-40' : 'flex flex-col'}`}>
                <h1
                  className={`font-bb ${isWeb ? 'text-40' : 'text-14'}`}
                  dangerouslySetInnerHTML={{
                    __html: authorInfo.name.replace(/\n/g, '<br>'),
                  }}
                />
                {authorInfo.occupation && (
                  <pre
                    className={`font-br whitespace-normal ${
                      isWeb
                        ? 'mt-12 text-16'
                        : `mt-10 text-12 ${waiMode ? 'text-primary-black' : 'text-[#808080]'}`
                    }`}
                    dangerouslySetInnerHTML={{
                      __html: authorInfo.occupation.replace(/\n/g, '<br>'),
                    }}
                  />
                )}
              </div>
            </div>
            {authorInfo.introduction && (
              <h5
                className={`font-ir ${
                  isWeb
                    ? 'ml-auto text-20 leading-[30px] w-[560px] [@media(max-width:1279px)]:w-[40%] [@media(max-width:980px)]:w-[50%] [@media(max-width:980px)]:text-16'
                    : 'mt-16 text-[13px] leading-[20px] whitespace-normal'
                }`}
                dangerouslySetInnerHTML={{
                  __html: authorInfo.introduction.replace(/\n/g, '<br>'),
                }}
              />
            )}
          </div>
        )}
      </div>

      {/* 文章列表 */}
      <AuthorArticleList
        articles={articles}
        page={page}
        total={total}
        loading={loading}
        authorInfo={authorInfo}
        navList={navList}
        isWeb={isWeb}
        onPageChange={handlePageChange}
        onLoadMore={handleLoadMore}
        onArticleExp={sdBlogAuthorDetailsPageArticleExp}
        onArticleClick={sdBlogAuthorDetailsPageArticleClick}
        onAuthorClick={sdBlogAuthorDetailsPageArticleClick}
      />
    </div>
  );
}
